import { ui } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";

import { formComponents as fc } from "../../../components/form";
import type { ScheduleT } from "../../../types/zod/timings";
import { processScheduleOptions } from "../../schedules/services/processScheduleOptions";
import UsersCombobox from "../../trackers/components/UsersCombobox";
import { useObservationMutation } from "../api/updateObservation";
import {
	type ObservationSchemaT,
	ObservationSchemaZ,
} from "../types/observationSchema";

interface Props {
	diaryId: string;
	observationId: string;
	observation?: ObservationSchemaT;
	schedules: ScheduleT[];
}

const ObservationForm = (props: Props) => {
	const updateObservation = useObservationMutation({
		diaryId: props.diaryId,
	});

	const form = useForm<ObservationSchemaT>({
		resolver: zodResolver(ObservationSchemaZ),
		defaultValues: {
			diary_id: props.diaryId,
			observation_id: props.observationId,
			observation_name: props.observation?.observation_name || "",
			observation_date:
				props.observation?.observation_date || new Date().toISOString(),
			schedule_id: props.observation?.schedule_id || "",
			form_id: props.observation?.form_id || "",
			contact_id: props.observation?.contact_id,
			shared_attribute_id: props.observation?.shared_attribute_id,
		},
	});

	const onSubmit: SubmitHandler<ObservationSchemaT> = async (formData) => {
		updateObservation.mutate(formData);
	};

	return (
		<ui.Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="form">
				<ui.FormField
					control={form.control}
					name="diary_id"
					render={() => (
						<ui.FormItem className="space-y-3">
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.FormField
					control={form.control}
					name="observation_id"
					render={() => (
						<ui.FormItem className="space-y-3">
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.FormField
					control={form.control}
					name="observation_name"
					render={({ field }) => (
						<ui.FormItem>
							<ui.FormLabel>Observation Name</ui.FormLabel>
							<ui.FormControl>
								<ui.Input {...field} placeholder="What is being observed?" />
							</ui.FormControl>
							<ui.FormDescription>What is being observed?</ui.FormDescription>
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.FormField
					control={form.control}
					name="observation_date"
					render={({ field }) => (
						<ui.FormItem>
							<ui.FormLabel>Observation Date</ui.FormLabel>
							<ui.FormControl>
								<ui.Input
									type="date"
									value={
										typeof field.value === "string"
											? field.value
											: field.value.toISOString().split("T")[0]
									}
									onChange={field.onChange}
									onBlur={field.onBlur}
									name={field.name}
								/>
							</ui.FormControl>
							<ui.FormDescription>
								When was this observation made?
							</ui.FormDescription>
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.FormField
					control={form.control}
					name="schedule_id"
					render={({ field }) => (
						<ui.FormItem>
							<ui.FormLabel>Schedule</ui.FormLabel>
							<ui.Select
								onValueChange={field.onChange}
								defaultValue={field.value}
							>
								<ui.SelectTrigger>
									<ui.SelectValue placeholder="Select a schedule" />
								</ui.SelectTrigger>
								<ui.SelectContent>
									{processScheduleOptions(props.schedules).map((option) => (
										<ui.SelectItem key={option.value} value={option.value}>
											{option.label}
										</ui.SelectItem>
									))}
								</ui.SelectContent>
							</ui.Select>
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.FormField
					control={form.control}
					name="contact_id"
					render={({ field }) => (
						<ui.FormItem>
							<ui.FormLabel>Contact</ui.FormLabel>
							<ui.Select
								onValueChange={field.onChange}
								defaultValue={field.value}
							>
								<ui.SelectTrigger>
									<ui.SelectValue placeholder="Select a contact" />
								</ui.SelectTrigger>
								<ui.SelectContent>
									{/* Contact options would be loaded here */}
								</ui.SelectContent>
							</ui.Select>
							<ui.FormMessage />
						</ui.FormItem>
					)}
				/>
				<ui.Button type="submit">Save Observation</ui.Button>
			</form>
		</ui.Form>
	);
};

export default ObservationForm;
